import {
  SKY_TWIN_TEACHING,
  stepSkyTwinLesson,
  skyTwinLessonTelemetry,
} from './130-sky-twin-tether.mjs';
import {
  SKY_VESPER_TEACHING,
  stepSkyVesperLesson,
  skyVesperLessonTelemetry,
} from './126-sky-vesper-lesson.mjs';
import { SKY_WORLD } from './86-sky-world-scale.mjs';

// Courses are the playable movement layer of the21-world preview campaign.
// Names describe route geometry; they never imply a boss signature is implemented.
const route = (name, mode, pattern, phase, count = 18) =>
  Object.freeze({ name, mode, pattern, phase, count, spacing: mode === 'bonanza' ? 25 : 34 });
const routes = Object.freeze({
  'cathedral-of-thunder': route('ROOFTOP SLALOM', 'navigation', 'climb', 0),
  'prism-widow': route('MIRROR THREAD', 'navigation', 'mirror', 0.6),
  cinderheart: route('CALDERA DIVE', 'sprint', 'dive', 1.2),
  'mourning-bride': route('WRECKAGE HARVEST', 'collection', 'weave', 1.4),
  'iron-orchid': Object.freeze({
    ...route('WATERFALL WEAVE', 'navigation', 'waterfall', 2),
    handoff:
      'At the flower: A/D orbits, W/S climbs. Circle through a side waterfall after pollen attaches.',
  }),
  'hourglass-king': route('HOURGLASS CIRCUIT', 'navigation', 'mirror', 2.2),
  'scrap-emperor': route('SALVAGE BONANZA', 'bonanza', 'spiral', 0, 24),
  vesper: Object.freeze({
    ...route('RESONANCE CORRIDOR', 'navigation', 'resonance', 0, 2),
    teaching: SKY_VESPER_TEACHING,
  }),
  'twin-executioners': route('TWIN CROSSING', 'sprint', 'mirror', 1.8),
  'abyssal-angler': route('BEACON DRIFT', 'navigation', 'climb', 0.8),
  'redline-express': route('REDLINE RAIL RUN', 'sprint', 'rail', 0),
  'dragon-sovereign': route('FOLD-FRAME THREAD', 'navigation', 'weave', 2.8),
  'mother-of-mines': route('NEST BONANZA', 'bonanza', 'spiral', 2.4, 24),
  'pale-surgeon': route('PRECISION ASCENT', 'navigation', 'climb', 1.8),
  'hollow-moon': route('CRATER DIVE', 'navigation', 'dive', 2.8),
  'ember-parliament': route('ANTENNA REDLINE', 'sprint', 'weave', 2.2),
  'deep-anchor': route('ELEVATOR ASCENT', 'navigation', 'climb', 3.4),
  'helios-unbound': route('HEAT-SHIELD SPRINT', 'sprint', 'waterfall', 0.8),
  'last-reflection': route('PATTERN BREAK', 'sprint', 'reflection', 0.4, 24),
});

/** Shared immutable movement profile for a current preview world. Opening
 * dragon/whale preserve their established journeys; unknown worlds return null. */
const twinCourse = Object.freeze({
  ...route('GANTRY PASSAGE', 'navigation', 'shipyard', 0, 1),
  teaching: SKY_TWIN_TEACHING,
});
export function skyFlightCourseProfile(stage) {
  if (stage?.worldId === 'twin-executioners' && stage.authoredBoss) return twinCourse;
  return typeof stage?.worldId === 'string' && Object.hasOwn(routes, stage.worldId)
    ? routes[stage.worldId]
    : null;
}
function gateCenter(profile, index) {
  const phase = index * 0.72 + profile.phase;
  switch (profile.pattern) {
    case 'climb':
      return [Math.sin(phase * 0.65) * 3.6, 4.7 + Math.cos(phase) * 2.1];
    case 'dive':
      return [Math.sin(phase * 0.65) * 4.2, 4.7 - Math.cos(phase) * 2.1];
    case 'rail':
      return [Math.sin(phase * 0.5) * 3.2, 3.8 + Math.sin(phase * 0.6) * 0.8];
    case 'mirror':
      return [Math.sin(phase) * 5.5, 4.3 + Math.abs(Math.cos(phase)) * 2.1];
    case 'waterfall':
      return [Math.sin(phase * 0.7) * 4.8, 4.7 + Math.sin(phase) * 2.1];
    case 'reflection': {
      const shift = Math.floor(index / 6) % 2 ? 0.9 : 0;
      return [Math.sin(phase + shift) * 5.2, 4.5 + Math.cos(phase * 0.5) * 1.5];
    }
    default:
      return [
        Math.sin(phase) * 6,
        profile.pattern === 'weave' ? 4.5 + Math.sin(phase * 0.5) : 4.7 + Math.cos(phase) * 2.1,
      ];
  }
}

/** Create a finite ordered gate ledger. Every opening remains inside the legal
 * steering corridor; adjacent centres need less than one second of base movement.
 * No seed reroll, returning wave or display quality change can create more gates. */
export function createSkyFlightCourse(sector, stage) {
  const profile = skyFlightCourseProfile(stage);
  if (!profile || ['resonance', 'shipyard'].includes(profile.pattern)) return null;
  const gates = Array.from({ length: profile.count }, (_, index) => {
    const [x, y] = gateCenter(profile, index);
    const distance = 72 + index * profile.spacing;
    return { index, x, y, z: SKY_WORLD.pilotZ - distance, distance, radius: 1.65, status: 'ahead' };
  });
  return {
    sector,
    profile,
    gates,
    distance: 0,
    elapsed: 0,
    resolved: 0,
    collected: 0,
    missed: 0,
    chain: 0,
    bestChain: 0,
    boost: 0,
    salvage: 0,
    complete: false,
    lastAward: null,
  };
}

/** Authored sprint speed and earned slipstream in metres/second. The terrain,
 * moving gates and approaching enemies consume this same physical speed. The
 * boost is bounded and never changes weapon cadence or guardian attack timing. */
export function skyFlightCourseSpeed(state, stage) {
  const profile = skyFlightCourseProfile(stage);
  if (!profile || state.startMode === 'boss' || state.boss || state.journey?.phase === 'boss')
    return stage.speed;
  const course = state.flightCourse?.sector === state.sector ? state.flightCourse : null;
  if (course?.complete) return stage.speed;
  return stage.speed * (profile.mode === 'sprint' ? 1.12 + (course?.boost ?? 0) * 0.12 : 1);
}

/** Resolve only real forward crossings, using swept pilot motion at the crossing
 * fraction. Call after pilot/terrain motion and before combat. Missing a gate
 * loses only its chain: the finite route continues and cannot trap a run. Owners
 * supply the canonical score feedback/currency callbacks; no purchase is forced.
 * Invalid/non-playing steps do nothing, and each gate can settle at most once. */
export function stepSkyFlightCourse(state, stage, dt, travelDelta, previousPilot, owners) {
  // Release prior-world source geometry before a successor lesson takes over.
  if (state.vesperLesson && state.vesperLesson.sector !== state.sector) delete state.vesperLesson;
  if (stage?.worldId === 'twin-executioners' && stage.authoredBoss) {
    stepSkyTwinLesson(state, dt, travelDelta, owners);
    return;
  }
  if (stage?.worldId === 'vesper') {
    stepSkyVesperLesson(state, dt, owners);
    return;
  }
  if (state.startMode === 'boss') return;
  if (state.status !== 'playing' || !Number.isFinite(dt) || dt <= 0 || dt > 0.1) return;
  if (!Number.isFinite(travelDelta) || travelDelta < 0 || !skyFlightCourseProfile(stage)) return;
  if (!state.flightCourse || state.flightCourse.sector !== state.sector)
    state.flightCourse = createSkyFlightCourse(state.sector, stage);
  const course = state.flightCourse;
  if (course.complete || state.boss || state.journey?.phase === 'boss') return;
  const before = course.distance;
  course.distance += travelDelta;
  course.elapsed += dt;
  course.boost = Math.max(0, course.boost - dt / 2.2);
  for (const gate of course.gates) {
    gate.z = SKY_WORLD.pilotZ + course.distance - gate.distance;
    if (gate.status !== 'ahead' || course.distance < gate.distance) continue;
    const fraction =
      travelDelta > 0 ? Math.max(0, Math.min(1, (gate.distance - before) / travelDelta)) : 1;
    const x = previousPilot.x + (state.x - previousPilot.x) * fraction;
    const y = previousPilot.y + (state.y - previousPilot.y) * fraction;
    const separation = Math.hypot(gate.x - x, gate.y - y);
    course.resolved++;
    if (separation <= gate.radius) collect(state, course, gate, separation, owners);
    else {
      gate.status = 'missed';
      course.missed++;
      course.chain = 0;
      owners.event(state, 'course-miss', 'GATE MISSED · NEXT GATE IS OPEN');
    }
  }
  if (course.resolved === course.gates.length) {
    course.complete = true;
    course.boost = 0;
    owners.event(
      state,
      'course-complete',
      `${course.profile.name} · ${course.collected}/${course.gates.length} · +${course.salvage} SALVAGE`,
    );
  }
}
function collect(state, course, gate, separation, { event, burst, earnSalvage }) {
  gate.status = 'collected';
  course.collected++;
  course.chain++;
  course.bestChain = Math.max(course.bestChain, course.chain);
  if (course.profile.mode === 'sprint') course.boost = 1;
  const precise = separation <= 0.55;
  const chainBonus = course.chain % 6 === 0 ? 2 : 0;
  const scoreAward = 150 + (precise ? 100 : 0) + Math.min(5, course.chain - 1) * 50;
  state.ringsCollected++;
  state.courseSalvageBonus = (state.courseSalvageBonus ?? 0) + chainBonus;
  state.score += scoreAward;
  course.salvage += 1 + chainBonus;
  earnSalvage(state, 1 + chainBonus);
  course.lastAward = {
    index: gate.index,
    time: state.time,
    scoreAward,
    precise,
    chain: course.chain,
    chainBonus,
  };
  burst(state, gate, 'gold', 'ring', { scoreAward, combo: 1 });
  event(
    state,
    'course-ring',
    `${precise ? 'PERFECT GATE' : 'GATE CLEAR'} · +${scoreAward}${chainBonus ? ' · +2 CHAIN SALVAGE' : ''}`,
  );
}

/** Read-only compact telemetry: show actual earned progress and the next physical
 * gate. The returned counts describe this stage only; no account unlock exists. */
export function skyFlightCourseTelemetry(state) {
  const lesson = skyTwinLessonTelemetry(state) ?? skyVesperLessonTelemetry(state);
  if (lesson) return lesson;
  const course = state?.flightCourse;
  if (!course || course.sector !== state.sector || state.boss || state.leviathan || state.cathedral)
    return null;
  const next = course.gates.find((gate) => gate.status === 'ahead');
  return {
    text: `${course.profile.name} · ${course.collected}/${course.gates.length}${course.chain > 1 ? ` · CHAIN ${course.chain}` : ''}${course.boost > 0 ? ' · SLIPSTREAM' : ''}${course.profile.handoff ? ' · AT FLOWER: A/D ORBIT · W/S CLIMB' : ''}`,
    label: `${course.profile.name}. ${course.collected} of ${course.gates.length} gates collected. ${course.missed} missed. ${course.salvage} salvage earned.${course.profile.handoff ? ` ${course.profile.handoff}` : ''}${next ? ` Next gate ${Math.ceil(Math.max(0, -next.z + SKY_WORLD.pilotZ))} metres ahead. Steer through the opening.` : ' Course complete.'}`,
    progress: course.resolved / course.gates.length,
    complete: course.complete,
  };
}
